import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SendPreviewDialog from '../../../changelog/components/editions/SendPreviewDialog';
import TemplateFields from './TemplateFields';
import TemplatePreviewPane from './TemplatePreviewPane';
import useTemplatePreview from '../../hooks/useTemplatePreview';
import { formFromTemplate, variantTitle } from '../../onboardingAdminConfig';

/**
 * Editor de um modelo: campos à esquerda, prévia ao vivo à direita.
 * A prévia por e-mail usa os campos como estão (salvos ou não) e vai só para o e-mail digitado.
 * @param {{ template: Object|null, lists: Object, onClose: Function, onSave: Function, notify: Function }} props
 *   lists: { targets, variables, blocks }; onSave(form, onDone(ok))
 */
function TemplateEditorDialog({ template, lists, onClose, onSave, notify }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewToOpen, setPreviewToOpen] = useState(false);
  const [mode, setMode] = useState('');
  const { preview, sendPreview, isSendingPreview } = useTemplatePreview(form, notify, mode);

  useEffect(() => {
    setForm(template ? formFromTemplate(template) : null);
    setMode('');
  }, [template]);

  if (!template || !form) return null;

  const change = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const save = () => {
    setSaving(true);
    onSave(form, (ok) => {
      setSaving(false);
      if (ok) onClose();
    });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div">
          {variantTitle(template)} · {form.key}
        </Typography>
        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>Editar e-mail</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 3 }}>
          <TemplateFields
            form={form}
            onChange={change}
            targets={lists.targets}
            variables={lists.variables}
            blocks={lists.blocks}
          />
          <TemplatePreviewPane preview={preview} modes={template.sample_modes} mode={mode} onModeChange={setMode} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Button startIcon={<SendOutlinedIcon />} disabled={isSendingPreview} onClick={() => setPreviewToOpen(true)}>
          {isSendingPreview ? 'Enviando prévia...' : 'Enviar prévia'}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={save}>Salvar</Button>
        </Box>
      </DialogActions>

      <SendPreviewDialog
        subtitle={form.subject || form.key}
        open={previewToOpen}
        onClose={() => setPreviewToOpen(false)}
        onSubmit={(to) => {
          setPreviewToOpen(false);
          sendPreview(to);
        }}
      />
    </Dialog>
  );
}

export default TemplateEditorDialog;
